import React, {useState} from 'react';
import './AdminModalsTestPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import QuestChestRewardModal from '../../Modals/QuestChestRewardModal/QuestChestRewardModal';
import FactionPurchaseRewardModal from '../../Modals/FactionPurchaseRewardModal/FactionPurchaseRewardModal';
import RankSkinRewardModal from '../../Modals/RankSkinRewardModal/RankSkinRewardModal';
import MatchWinnerModal from '../../Modals/MatchWinnerModal/MatchWinnerModal';
import BotMatchPromoModal from '../../Modals/BotMatchPromoModal/BotMatchPromoModal';
import OnlineMatchPromoModal from '../../Modals/OnlineMatchPromoModal/OnlineMatchPromoModal';
import RankedMatchPromoModal from '../../Modals/RankedMatchPromoModal/RankedMatchPromoModal';
import RankSkinsNewsModal from '../../Modals/RankSkinsNewsModal/RankSkinsNewsModal';
import AiMatchPromoModal from '../../Modals/AiMatchPromoModal/AiMatchPromoModal';
import AiMatchNewsModal from '../../Modals/AiMatchNewsModal/AiMatchNewsModal';
import AiLobbySpectatorModal from '../../Modals/AiLobbySpectatorModal/AiLobbySpectatorModal';
import {AI_PROMO_STATUSES} from '../../../configs/aiMatch';
import AppInstallPromoModal from '../../Modals/AppInstallPromoModal/AppInstallPromoModal';
import AppInstallRewardModal from '../../Modals/AppInstallRewardModal/AppInstallRewardModal';
import {APP_INSTALL_REWARD_CHEST_ID} from '../../../configs/appInstall';
import {BOT_MATCH_PROMO_REASONS} from '../../../utils/menuPromo/resolveBotMatchPromoReason';
import {ONLINE_MATCH_PROMO_REASONS} from '../../../utils/menuPromo/resolveOnlineMatchPromoReason';
import {RANKED_MATCH_PROMO_REASONS} from '../../../utils/menuPromo/resolveRankedMatchPromoReason';
import {getOnlineMatchQuestReward} from '../../../utils/quests/getOnlineMatchQuestReward';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {
  createRandomMatchWinnerPreview,
  getMatchWinnerPreviews,
  getQuestRewardPreviews,
  getRankSkinRewardPreviews,
  REWARD_MODAL_KINDS,
} from '../../../utils/quests/getMatchEndRewardPreviews';

const PAGE_ID = 'AdminModalsTestPage';

const MINUTE_MS = 60 * 1000;
// Лобби дня для превью промо «Битвы с AI» (в виде normalizeAiPromo):
//  - сбор игроков — до планового старта 5 минут, в лобби один живой;
//  - идет отсчет — плановый старт прошел, сервер взвел отсчет на 7 минут
const AI_MATCH_PROMO_PREVIEW_KINDS = {
  GATHERING: 'gathering',
  COUNTDOWN: 'countdown',
};
const buildAiMatchPromoPreview = (kind, now = Date.now()) => {
  const isCountdown = kind === AI_MATCH_PROMO_PREVIEW_KINDS.COUNTDOWN;
  const opensAt = isCountdown ? now - MINUTE_MS : now + 5 * MINUTE_MS;
  const startAt = isCountdown ? now + 7 * MINUTE_MS : null;
  return {
    lobbyId: 'preview',
    status: isCountdown ? AI_PROMO_STATUSES.COUNTDOWN : AI_PROMO_STATUSES.WAITING,
    opensAt,
    startAt,
    projectedStartAt: startAt ?? opensAt + 10 * MINUTE_MS,
    latestStartAt: opensAt + 10 * MINUTE_MS,
    cutoffAt: opensAt + 120 * MINUTE_MS,
    liveCount: isCountdown ? 2 : 1,
    maxPlayers: 6,
    spectatorsCount: 0,
  };
};

/**
 * Админский предпросмотр наградных модалок конца матча. Пройти до них живьем
 * дорого: нужен настоящий матч, а часть наград выдается один раз за аккаунт
 * (первый сетевой, первый рейтинговый, каждая лига) — увидеть их повторно
 * без правки базы нельзя вовсе.
 *
 * Модалки открываются с теми же параметрами, что в бою: список собран из
 * конфигов заданий (см. getMatchEndRewardPreviews), поэтому после правки
 * наград предпросмотр меняется сам.
 *
 * Здесь же — модалки главного меню, которые показываются по одному разу
 * на событие (приглашения в первый обычный матч после обучения и в первый
 * рейтинговый): дождаться их живьем тоже нельзя.
 *
 * Ничего не начисляет и в стор не пишет — это только визуальная проверка.
 */
function AdminModalsTestPage({onBack}) {
  const [activePreview, setActivePreview] = useState(null);
  // Модалки меню открываются своим флагом: у них нет параметров-превью
  const [isBotMatchPromoOpen, setBotMatchPromoOpen] = useState(false);
  const [isOnlineMatchPromoOpen, setOnlineMatchPromoOpen] = useState(false);
  const [isRankedMatchPromoOpen, setRankedMatchPromoOpen] = useState(false);
  // Новость о рейтинговых обличиях показывается игроку один раз — живьем
  // ее после этого не увидеть. Превью отметку показа на сервер не ставит
  const [isRankSkinsNewsOpen, setRankSkinsNewsOpen] = useState(false);
  // Промо «Битвы с AI Claude Opus» живьем видно только вечером, пока лобби дня
  // ждет игроков, и раз за игровой день. Превью отметку показа не ставит.
  // Две строки старта: «Сбор игроков» до планового старта и «Старт через»,
  // когда отсчет взведен (null — превью закрыто)
  const [aiMatchPromoPreview, setAiMatchPromoPreview] = useState(null);
  // Анонс события: первый показ и повтор для закрывших прежнюю редакцию
  // («новое время»); null — превью закрыто
  const [aiMatchNewsPreview, setAiMatchNewsPreview] = useState(null);
  // Вход зрителем в заполненное лобби с ИИ: живьем нужно шесть участников
  const [isAiLobbySpectatorOpen, setAiLobbySpectatorOpen] = useState(false);
  // Установка приложения: предложение видно только там, где браузер прислал
  // beforeinstallprompt, а поздравление — ровно один раз на аккаунт
  const [isAppInstallPromoOpen, setAppInstallPromoOpen] = useState(false);
  const [isAppInstallRewardOpen, setAppInstallRewardOpen] = useState(false);
  // Последний случайный победитель — его параметры видны под кнопкой, чтобы
  // заметный на превью дефект можно было повторить
  const [lastRandomWinner, setLastRandomWinner] = useState(null);

  const openRandomWinner = () => {
    const preview = createRandomMatchWinnerPreview();
    // Новый ключ на каждое открытие: модалка заново выбирает поздравление
    const numberedPreview = {...preview, id: `${preview.id}_${Date.now()}`};
    setLastRandomWinner(numberedPreview);
    setActivePreview(numberedPreview);
  };

  usePageAnalytics(PAGE_ID);

  const questPreviews = getQuestRewardPreviews();
  const rankSkinPreviews = getRankSkinRewardPreviews();
  const matchWinnerPreviews = getMatchWinnerPreviews();

  const closeModal = () => setActivePreview(null);

  const renderPreviewButtons = (previews) => previews.map((preview) => (
    <PageButton
      key={preview.id}
      className={'admin-modals-test-button'}
      pageId={PAGE_ID}
      buttonId={preview.id}
      onClick={() => setActivePreview(preview)}
    >
      {preview.title}
    </PageButton>
  ));

  return (
    <>
      <h1 className="lobby-page-title">Тест модалок</h1>

      <p className="admin-modals-test-hint">
        Наградные модалки конца матча и разовые модалки главного меню — открываются как есть,
        ничего не начисляя
      </p>

      <h2 className="admin-modals-test-section-title">Главное меню</h2>
      <div className="admin-modals-test-buttons">
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'bot_match_promo'}
          onClick={() => setBotMatchPromoOpen(true)}
        >
          Обучение пройдено — приглашение в бой с ботами
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'online_match_promo'}
          onClick={() => setOnlineMatchPromoOpen(true)}
        >
          Бой с ботами сыгран — приглашение в обычный матч
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'ranked_match_promo'}
          onClick={() => setRankedMatchPromoOpen(true)}
        >
          Первый сетевой матч сыгран — приглашение в рейтинг
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'app_install_promo'}
          onClick={() => setAppInstallPromoOpen(true)}
        >
          Предложение установить приложение
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'app_install_reward'}
          onClick={() => setAppInstallRewardOpen(true)}
        >
          Награда за первый вход через приложение
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'rank_skins_news'}
          onClick={() => setRankSkinsNewsOpen(true)}
        >
          Новость о рейтинговых обличиях
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'ai_match_promo'}
          onClick={() => setAiMatchPromoPreview(buildAiMatchPromoPreview(AI_MATCH_PROMO_PREVIEW_KINDS.GATHERING))}
        >
          Битва с AI Claude Opus — приглашение, сбор игроков
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'ai_match_promo_countdown'}
          onClick={() => setAiMatchPromoPreview(buildAiMatchPromoPreview(AI_MATCH_PROMO_PREVIEW_KINDS.COUNTDOWN))}
        >
          Битва с AI Claude Opus — приглашение, идет отсчет
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'ai_match_news'}
          onClick={() => setAiMatchNewsPreview({isUpdate: false})}
        >
          Битва с AI Claude Opus — разовый анонс события
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'ai_match_news_update'}
          onClick={() => setAiMatchNewsPreview({isUpdate: true})}
        >
          Битва с AI Claude Opus — анонс нового времени
        </PageButton>
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'ai_lobby_spectator'}
          onClick={() => setAiLobbySpectatorOpen(true)}
        >
          Битва с AI Claude Opus — лобби заполнено, вход зрителем
        </PageButton>
      </div>

      <h2 className="admin-modals-test-section-title">Победитель матча</h2>
      <div className="admin-modals-test-buttons">
        <PageButton
          className={'admin-modals-test-button'}
          pageId={PAGE_ID}
          buttonId={'match_winner_random'}
          onClick={openRandomWinner}
        >
          Случайный победитель
        </PageButton>
        {lastRandomWinner && (
          <p className="admin-modals-test-hint admin-modals-test-random-params">
            {lastRandomWinner.title}
          </p>
        )}
        {renderPreviewButtons(matchWinnerPreviews)}
      </div>

      <h2 className="admin-modals-test-section-title">Награды за задания</h2>
      <div className="admin-modals-test-buttons">
        {renderPreviewButtons(questPreviews)}
      </div>

      <h2 className="admin-modals-test-section-title">Ранговые образы</h2>
      <div className="admin-modals-test-buttons">
        {renderPreviewButtons(rankSkinPreviews)}
      </div>

      <div className="main-menu-buttons admin-modals-test-page-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>

      {activePreview?.kind === REWARD_MODAL_KINDS.CHEST && (
        <QuestChestRewardModal
          key={activePreview.id}
          chestId={activePreview.chestId}
          onOpenInventory={closeModal}
          onMoreChests={closeModal}
          onClose={closeModal}
        />
      )}

      {activePreview?.kind === REWARD_MODAL_KINDS.FACTION && (
        <FactionPurchaseRewardModal
          key={activePreview.id}
          factionId={activePreview.factionId}
          onClaim={closeModal}
        />
      )}

      {activePreview?.kind === REWARD_MODAL_KINDS.MATCH_WINNER && (
        <MatchWinnerModal
          key={activePreview.id}
          player={activePreview.player}
          finalWinPoints={activePreview.finalWinPoints}
          isRanked={activePreview.isRanked}
          ratingDelta={activePreview.ratingDelta}
          rating={activePreview.rating}
          onClose={closeModal}
        />
      )}

      {activePreview?.kind === REWARD_MODAL_KINDS.RANK_SKIN && (
        <RankSkinRewardModal
          key={activePreview.id}
          skinId={activePreview.skinId}
          onClaim={closeModal}
        />
      )}

      {/* Кнопки матчей здесь никуда не ведут — только закрывают превью;
          награда за первый матч — как у игрока без открытых фракций */}
      {isAppInstallPromoOpen && (
        <AppInstallPromoModal
          onInstall={() => {}}
          onClose={() => setAppInstallPromoOpen(false)}
        />
      )}

      {isAppInstallRewardOpen && (
        <AppInstallRewardModal
          chestId={APP_INSTALL_REWARD_CHEST_ID}
          onOpenInventory={() => setAppInstallRewardOpen(false)}
          onClose={() => setAppInstallRewardOpen(false)}
        />
      )}

      {isBotMatchPromoOpen && (
        <BotMatchPromoModal
          reason={BOT_MATCH_PROMO_REASONS.TUTORIAL_FINISHED}
          onBotMatch={() => {}}
          onClose={() => setBotMatchPromoOpen(false)}
        />
      )}

      {isOnlineMatchPromoOpen && (
        <OnlineMatchPromoModal
          reason={ONLINE_MATCH_PROMO_REASONS.FIRST}
          firstOnlineMatchReward={getOnlineMatchQuestReward([])}
          onFindOpponents={() => {}}
          onClose={() => setOnlineMatchPromoOpen(false)}
        />
      )}

      {isRankedMatchPromoOpen && (
        <RankedMatchPromoModal
          reason={RANKED_MATCH_PROMO_REASONS.FIRST}
          onRankedGame={() => {}}
          onClose={() => setRankedMatchPromoOpen(false)}
        />
      )}

      {isRankSkinsNewsOpen && (
        <RankSkinsNewsModal
          onOpenCollection={() => {}}
          onClose={() => setRankSkinsNewsOpen(false)}
        />
      )}

      {aiMatchPromoPreview && (
        <AiMatchPromoModal
          aiPromo={aiMatchPromoPreview}
          onJoin={() => {}}
          onClose={() => setAiMatchPromoPreview(null)}
        />
      )}

      {aiMatchNewsPreview && (
        <AiMatchNewsModal isUpdate={aiMatchNewsPreview.isUpdate} onClose={() => setAiMatchNewsPreview(null)}/>
      )}

      {isAiLobbySpectatorOpen && (
        <AiLobbySpectatorModal onHide={() => setAiLobbySpectatorOpen(false)}/>
      )}
    </>
  );
}

export default AdminModalsTestPage;
